import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './useAuth';

export function useTasks() {
  const { currentUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real-time listener with ownerId filter
  useEffect(() => {
    if (!currentUser) {
      setTasks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(
      collection(db, 'tasks'),
      where('ownerId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const tasksData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTasks(tasksData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching tasks:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Add new task
  const addTask = async (taskData) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to add tasks');
    }

    try {
      const docRef = await addDoc(collection(db, 'tasks'), {
        ...taskData,
        ownerId: currentUser.uid,
        status: taskData.status || 'todo', // todo, in_progress, completed
        completed: false,
        completedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (err) {
      console.error('Error adding task:', err);
      throw err;
    }
  };

  // Update existing task
  const updateTask = async (taskId, updates) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to update tasks');
    }

    try {
      const taskRef = doc(db, 'tasks', taskId);
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };

      // Set completedAt when status changes to completed
      if (updates.status === 'completed' && !updates.completed) {
        updateData.completed = true;
        updateData.completedAt = serverTimestamp();
      } else if (updates.status && updates.status !== 'completed') {
        updateData.completed = false;
        updateData.completedAt = null;
      }

      await updateDoc(taskRef, updateData);
    } catch (err) {
      console.error('Error updating task:', err);
      throw err;
    }
  };

  // Toggle task completion status
  const toggleTask = async (taskId, currentStatus) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to toggle tasks');
    }

    try {
      const taskRef = doc(db, 'tasks', taskId);
      const newStatus = currentStatus ? 'todo' : 'completed';
      await updateDoc(taskRef, {
        completed: !currentStatus,
        status: newStatus,
        completedAt: !currentStatus ? serverTimestamp() : null,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error toggling task:', err);
      throw err;
    }
  };

  // Delete task
  const deleteTask = async (taskId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to delete tasks');
    }

    try {
      const taskRef = doc(db, 'tasks', taskId);
      await deleteDoc(taskRef);
    } catch (err) {
      console.error('Error deleting task:', err);
      throw err;
    }
  };

  return {
    tasks,
    loading,
    error,
    addTask,
    updateTask,
    toggleTask,
    deleteTask
  };
}
